import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../templates/AdminLayout';
import AppButton from '../atoms/AppButton';
import AppBadge from '../atoms/AppBadge';
import AppSpinner from '../atoms/AppSpinner';
import { IconPlusCircle, IconPencil, IconTrash, IconChevronDown } from '../atoms/Icon';
import AddonGroupFormModal from '../organisms/AddonGroupFormModal';
import useConfirmDialog from '../organisms/ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import { listAddonGroups, deleteAddonGroup, updateAddonGroup } from '../api/addonGroupsApi';
import { listCategories, listProducts } from '../api/menuApi';

const byOrder = (a, b) => a.sortOrder - b.sortOrder;

function reorder(list, from, to) {
  const copy = [...list];
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

function AddonGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formModal, setFormModal] = useState(null);
  const { confirm, dialog } = useConfirmDialog();
  const toast = useToast();

  // Produtos/categorias já existem independente do épico — carregá-los junto
  // com `Promise.all` faria um 404 só do endpoint novo (ainda não implantado
  // pelo backend) derrubar o formulário inteiro sem produto nenhum pra
  // escolher. Cada busca falha por conta própria.
  const load = useCallback(async () => {
    const [gsResult, prodsResult, catsResult] = await Promise.allSettled([
      listAddonGroups(), listProducts(), listCategories(),
    ]);
    if (gsResult.status === 'fulfilled') {
      setGroups([...gsResult.value].sort(byOrder));
      setError(null);
    } else {
      setError(gsResult.reason.message);
    }
    if (prodsResult.status === 'fulfilled') setProducts(prodsResult.value);
    if (catsResult.status === 'fulfilled') setCategories(catsResult.value);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const nextSortOrder = useCallback(
    () => groups.reduce((max, g) => Math.max(max, g.sortOrder + 1), 0),
    [groups],
  );

  async function persistOrder(requests) {
    try {
      await Promise.all(requests);
    } catch (e) {
      toast.error(`Não foi possível salvar a nova ordem: ${e.message}`);
      load();
    }
  }

  function moveGroup(group, direction) {
    const list = [...groups].sort(byOrder);
    const from = list.findIndex((g) => g.id === group.id);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= list.length) return;

    const touched = reorder(list, from, to).map((g, index) => ({ ...g, sortOrder: index }));
    const before = new Map(list.map((g) => [g.id, g.sortOrder]));
    setGroups(touched);
    persistOrder(
      touched
        .filter((g) => before.get(g.id) !== g.sortOrder)
        .map((g) => updateAddonGroup(g.id, { sortOrder: g.sortOrder })),
    );
  }

  async function handleDelete(group) {
    const ok = await confirm(`Excluir o grupo "${group.name}"? Os adicionais dele continuam existindo, só deixam de ter o limite/gratuidade deste grupo.`, {
      title: 'Excluir grupo de adicionais',
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteAddonGroup(group.id);
      load();
      toast.success('Grupo de adicionais excluído com sucesso!');
    } catch (e) {
      toast.error(e.message);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <AppSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="d-flex justify-content-between align-items-center gap-2 mb-2 flex-wrap">
        <h4 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>Grupos de adicionais</h4>
        <div className="d-flex gap-2">
          <button type="button" className="action-btn" onClick={() => navigate('/cardapio')}>
            Voltar ao cardápio
          </button>
          <AppButton variant="primary" onClick={() => setFormModal('new')} className="d-flex align-items-center gap-1">
            <IconPlusCircle size={15} /> Novo grupo
          </AppButton>
        </div>
      </div>
      <p className="mb-4" style={{ fontSize: 13, color: '#888' }}>
        Escolha produtos e, entre os adicionais já cadastrados neles, quantos o cliente pode selecionar e quantos são gratuitos.
      </p>

      {error && (
        <div className="alert alert-danger" style={{ borderRadius: 10 }}>
          Não foi possível carregar os grupos de adicionais: {error}
        </div>
      )}

      {groups.length === 0 && !error && (
        <div className="text-center py-5 text-muted">
          <p className="mb-3">Nenhum grupo de adicionais cadastrado ainda.</p>
          <AppButton variant="primary" onClick={() => setFormModal('new')} className="mx-auto">
            Criar o primeiro grupo
          </AppButton>
        </div>
      )}

      {groups.map((group, index) => {
        const productNames = (group.productIds || [])
          .map((id) => productById.get(id)?.name)
          .filter(Boolean);

        return (
          <div key={group.id} className="card-elevated border-0 mb-2" style={{ borderRadius: 'var(--radius-md)', padding: '14px 18px' }}>
            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
              <div style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-semibold" style={{ color: 'var(--color-neutral)' }}>{group.name}</span>
                  <AppBadge>até {group.maxSelection}</AppBadge>
                  {group.freeQuantity > 0 && <AppBadge variant="amber">{group.freeQuantity} grátis</AppBadge>}
                  <span style={{ fontSize: 12, color: '#aaa' }}>
                    {group.addons?.length || 0} adicional(is)
                  </span>
                </div>
                {group.description && (
                  <div style={{ fontSize: 12.5, color: '#888', marginTop: 4 }}>{group.description}</div>
                )}
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
                  {productNames.length ? productNames.join(', ') : 'Nenhum produto vinculado'}
                </div>
              </div>

              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                {index > 0 && (
                  <button type="button" className="action-btn icon-btn action-btn-muted" onClick={() => moveGroup(group, -1)} title="Mover para cima" aria-label="Mover para cima">
                    <IconChevronDown size={15} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                )}
                {index < groups.length - 1 && (
                  <button type="button" className="action-btn icon-btn action-btn-muted" onClick={() => moveGroup(group, 1)} title="Mover para baixo" aria-label="Mover para baixo">
                    <IconChevronDown size={15} />
                  </button>
                )}
                <button type="button" className="action-btn icon-btn action-btn-muted" onClick={() => setFormModal(group)} title="Editar grupo" aria-label="Editar grupo">
                  <IconPencil size={15} />
                </button>
                <button type="button" className="action-btn icon-btn action-btn-danger" onClick={() => handleDelete(group)} title="Excluir grupo" aria-label="Excluir grupo">
                  <IconTrash size={15} />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {formModal && (
        <AddonGroupFormModal
          show={!!formModal}
          group={formModal === 'new' ? null : formModal}
          groups={groups}
          products={products}
          categories={categories}
          nextSortOrder={nextSortOrder}
          onHide={() => setFormModal(null)}
          onSaved={load}
        />
      )}

      {dialog}
    </AdminLayout>
  );
}

export default AddonGroupsPage;
